import React, { useEffect, useState, useCallback } from 'react';
import { formatDateTime } from '@/utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, 
  Clock, 
  Play, 
  Pause, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle,
  Bell,
  Package,
  FileText,
  Boxes,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function SchedulerPage() {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningTask, setRunningTask] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [statusRes, previewRes] = await Promise.all([
        api.get('/scheduler/status'),
        api.get('/scheduler/alertas/preview')
      ]);
      setStatus(statusRes.data);
      setPreview(previewRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do scheduler');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Atualizar a cada 30 segundos
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleStartScheduler = async () => {
    try {
      await api.post('/scheduler/start');
      toast.success('Scheduler iniciado');
      loadData();
    } catch (error) {
      toast.error('Erro ao iniciar scheduler');
    }
  };

  const handleStopScheduler = async () => {
    try {
      await api.post('/scheduler/stop');
      toast.success('Scheduler parado');
      loadData();
    } catch (error) {
      toast.error('Erro ao parar scheduler');
    }
  };

  const handleRunTask = async (taskName) => {
    setRunningTask(taskName);
    try {
      const response = await api.post(`/scheduler/run/${taskName}`);
      toast.success(`Tarefa "${taskName}" executada com sucesso`);
      
      // Mostrar resultado
      const result = response.data.result;
      if (taskName === 'materiais') {
        toast.info(`${result.ciclos_vencendo || 0} ciclos vencendo, ${result.alertas_enviados || 0} alertas enviados`);
      } else if (taskName === 'pops') {
        toast.info(`${result.pops_vencidos || 0} POPs vencidos, ${result.pops_vencendo || 0} POPs vencendo`);
      } else if (taskName === 'estoque') {
        toast.info(`${result.total_alertas || 0} alertas de estoque encontrados`);
      }
      
      loadData();
    } catch (error) {
      toast.error(`Erro ao executar tarefa: ${error.response?.data?.detail || error.message}`);
    } finally {
      setRunningTask(null);
    }
  };

  // Função auxiliar para mapear job.id para taskName
  const getTaskName = (jobId) => {
    const mapping = {
      'check_materiais_vencendo': 'materiais',
      'check_pops_vencendo': 'pops',
      'check_estoque_alertas': 'estoque'
    };
    return mapping[jobId] || jobId;
  };

  // Função para obter ícone do job
  const getJobIcon = (jobId) => {
    if (jobId === 'check_materiais_vencendo') return <Package className="w-5 h-5 text-amber-600" />;
    if (jobId === 'check_pops_vencendo') return <FileText className="w-5 h-5 text-purple-600" />;
    if (jobId === 'check_estoque_alertas') return <Boxes className="w-5 h-5 text-emerald-600" />;
    return <Clock className="w-5 h-5 text-slate-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scheduler-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Agendador de Tarefas</h1>
          <p className="text-slate-500">Gerenciamento de verificações automáticas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          {status?.status === 'running' ? (
            <Button variant="destructive" onClick={handleStopScheduler} data-testid="stop-scheduler-btn">
              <Pause className="w-4 h-4 mr-2" />
              Parar Scheduler
            </Button>
          ) : (
            <Button onClick={handleStartScheduler} data-testid="start-scheduler-btn">
              <Play className="w-4 h-4 mr-2" />
              Iniciar Scheduler
            </Button>
          )}
        </div>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Status do Scheduler
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <Badge variant={status?.status === 'running' ? 'default' : 'secondary'} className="text-sm">
              {status?.status === 'running' ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Rodando
                </>
              ) : (
                <>
                  <Pause className="w-4 h-4 mr-1" />
                  Parado
                </>
              )}
            </Badge>
            {status?.last_run_results?.last_check && (
              <span className="text-sm text-slate-500">
                Última verificação: {formatDateTime(status.last_run_results.last_check)}
              </span>
            )}
          </div>

          {/* Jobs programados */}
          <div className="space-y-3">
            <h4 className="font-semibold text-slate-700">Tarefas Programadas:</h4>
            {status?.jobs?.map((job) => {
              const taskName = getTaskName(job.id);
              return (
                <div key={job.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {getJobIcon(job.id)}
                    <div>
                      <p className="font-medium text-slate-900">{job.name}</p>
                      <p className="text-sm text-slate-500">
                        Próxima execução: {job.next_run ? formatDateTime(job.next_run) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleRunTask(taskName)}
                    disabled={runningTask !== null}
                    data-testid={`run-${job.id}-btn`}
                  >
                    {runningTask === taskName ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-1" />
                        Executar Agora
                      </>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Preview de Alertas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Materiais Vencendo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              Materiais Vencendo (7 dias)
              <Badge variant="outline" className="ml-auto">
                {preview?.materiais_vencendo?.total || 0}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preview?.materiais_vencendo?.total > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {preview.materiais_vencendo.itens.map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      item.urgencia === 'alta' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{item.cliente}</span>
                      <Badge variant={item.urgencia === 'alta' ? 'destructive' : 'secondary'}>
                        {item.dias_restantes} dias
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600">Ciclo: {item.ciclo}</p>
                    <p className="text-xs text-slate-500">
                      {item.email && `📧 ${item.email}`} {item.telefone && `📱 ${item.telefone}`}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>Nenhum material vencendo nos próximos 7 dias</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* POPs Vencendo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              POPs com Alerta (30 dias)
              <Badge variant="outline" className="ml-auto">
                {preview?.pops_alerta?.total || 0}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preview?.pops_alerta?.total > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {preview.pops_alerta.itens.map((pop, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      pop.status === 'vencido' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{pop.codigo}</span>
                      <Badge variant={pop.status === 'vencido' ? 'destructive' : 'secondary'}>
                        {pop.status === 'vencido' ? `Vencido há ${Math.abs(pop.dias_restantes)} dias` : `${pop.dias_restantes} dias`}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600">{pop.titulo}</p>
                    <p className="text-xs text-slate-500">Área: {pop.area}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>Nenhum POP vencido ou vencendo</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alertas de Estoque */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Boxes className="w-5 h-5 text-emerald-600" />
              Alertas de Estoque
              <Badge variant="outline" className="ml-auto">
                {preview?.estoque_alertas?.total || 0}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preview?.estoque_alertas?.total > 0 ? (
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {/* Estoque Baixo */}
                {preview.estoque_alertas.itens?.estoque_minimo?.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">
                      Estoque Baixo ({preview.estoque_alertas.estoque_baixo})
                    </h5>
                    {preview.estoque_alertas.itens.estoque_minimo.slice(0, 5).map((item, idx) => (
                      <div 
                        key={idx} 
                        className={`p-2 rounded-lg border mb-2 ${
                          item.urgencia === 'critico' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{item.produto}</span>
                          <Badge variant={item.urgencia === 'critico' ? 'destructive' : 'secondary'} className="text-xs">
                            {item.urgencia === 'critico' ? 'CRÍTICO' : 'Alerta'}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-600">
                          Atual: {item.quantidade_atual} | Mín: {item.quantidade_minima}
                        </p>
                      </div>
                    ))}
                    {preview.estoque_alertas.itens.estoque_minimo.length > 5 && (
                      <p className="text-xs text-slate-500 text-center">
                        +{preview.estoque_alertas.itens.estoque_minimo.length - 5} mais...
                      </p>
                    )}
                  </div>
                )}
                
                {/* Lotes Vencendo */}
                {preview.estoque_alertas.itens?.vencimento_proximo?.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">
                      Lotes Vencendo ({preview.estoque_alertas.lotes_vencendo})
                    </h5>
                    {preview.estoque_alertas.itens.vencimento_proximo.slice(0, 5).map((item, idx) => (
                      <div 
                        key={idx} 
                        className={`p-2 rounded-lg border mb-2 ${
                          item.urgencia === 'critico' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{item.produto}</span>
                          <Badge variant={item.urgencia === 'critico' ? 'destructive' : 'secondary'} className="text-xs">
                            {item.dias_restantes} dias
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-600">
                          Lote: {item.lote} | Qtd: {item.quantidade}
                        </p>
                      </div>
                    ))}
                    {preview.estoque_alertas.itens.vencimento_proximo.length > 5 && (
                      <p className="text-xs text-slate-500 text-center">
                        +{preview.estoque_alertas.itens.vencimento_proximo.length - 5} mais...
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>Estoque em dia</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Últimos Resultados */}
      {status?.last_run_results && (status.last_run_results.materiais_vencendo || status.last_run_results.pops_vencendo || status.last_run_results.estoque_alertas) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              Últimos Resultados das Verificações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {status.last_run_results.materiais_vencendo && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-semibold text-amber-800 mb-2">Materiais</h4>
                  <div className="text-sm space-y-1">
                    <p>Ciclos vencendo: {status.last_run_results.materiais_vencendo.ciclos_vencendo || 0}</p>
                    <p>Arsenal vencendo: {status.last_run_results.materiais_vencendo.arsenal_vencendo || 0}</p>
                    <p>Alertas enviados: {status.last_run_results.materiais_vencendo.alertas_enviados || 0}</p>
                    <p className="text-xs text-slate-500">
                      {status.last_run_results.materiais_vencendo.timestamp && formatDateTime(status.last_run_results.materiais_vencendo.timestamp)}
                    </p>
                  </div>
                </div>
              )}
              {status.last_run_results.pops_vencendo && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-semibold text-purple-800 mb-2">POPs</h4>
                  <div className="text-sm space-y-1">
                    <p>POPs vencidos: {status.last_run_results.pops_vencendo.pops_vencidos || 0}</p>
                    <p>POPs vencendo: {status.last_run_results.pops_vencendo.pops_vencendo || 0}</p>
                    <p className="text-xs text-slate-500">
                      {status.last_run_results.pops_vencendo.timestamp && formatDateTime(status.last_run_results.pops_vencendo.timestamp)}
                    </p>
                  </div>
                </div>
              )}
              {status.last_run_results.estoque_alertas && (
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <h4 className="font-semibold text-emerald-800 mb-2">Estoque</h4>
                  <div className="text-sm space-y-1">
                    <p>Total de alertas: {status.last_run_results.estoque_alertas.alertas?.total_alertas || 0}</p>
                    <p>Estoque baixo: {status.last_run_results.estoque_alertas.alertas?.estoque_minimo?.length || 0}</p>
                    <p>Lotes vencendo: {status.last_run_results.estoque_alertas.alertas?.vencimento_proximo?.length || 0}</p>
                    <p className="text-xs text-slate-500">
                      {status.last_run_results.estoque_alertas.timestamp && formatDateTime(status.last_run_results.estoque_alertas.timestamp)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
